import { DeleteOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Table } from 'antd'
import styled from '@emotion/styled'
import React from 'react'
import { createSimpleStore } from 'react-simple-reducer'
import { toast } from 'react-toastify'

import api, { ICounter } from '../../api'
import { DownloadModel } from '../../models/DownloadModel'
import { useAuthState } from '../../stores/authStore'

interface DownloadsCursosViewProps {
  cursoId: number
}

const Store = createSimpleStore(
  {
    cursoId: 0,
    downloads: [] as DownloadModel[],
    downloadsCount: 0,
    fetching: false,
    creatingDownload: false,
    query: { limit: 1000, offset: 0 },
    deletingDownloadId: null as number | null,
  },
  {
    init(state, payload: { cursoId: number }) {
      state.cursoId = payload.cursoId
    },
    downloadCreateOrUpdateSuccess(state, download: DownloadModel) {
      const edicao = state.downloads.some((d) => d.id === download.id)
      if (!edicao) {
        state.downloadsCount = state.downloadsCount + 1
        state.downloads.push(download)
      } else {
        state.downloads = state.downloads.map((d) => (download.id === d.id ? download : d))
      }
    },
    downloadsDeleteSuccess(state, downloadId: number) {
      state.downloads = state.downloads.filter((c) => +c.id !== downloadId)
      state.downloadsCount = state.downloadsCount - 1
      state.deletingDownloadId = null
    },
    setQueryParams(state, query: { limit?: number; offset?: number }) {
      state.query = { ...state.query, ...query }
    },
    getDownloadsByCursoStarted(state) {
      state.fetching = true
    },
    getDownloadsByCursoSuccess(state, responseDownloads: ICounter<DownloadModel>) {
      state.downloads = responseDownloads.rows
      state.downloadsCount = responseDownloads.count
      state.fetching = false
    },
    getDownloadsByCursoError(state) {
      state.fetching = false
    },
    deleteDownloadStarted(state, downloadId: number) {
      state.deletingDownloadId = downloadId
    },
    deleteDownloadError(state) {
      state.deletingDownloadId = null
    },
    createDownloadStarted(state) {
      state.creatingDownload = true
    },
    createDownloadFinished(state) {
      state.creatingDownload = false
    },
  },
  {
    thunks: {
      getDownloadsByCurso({ cursoId }) {
        return async (dispatch, getState) => {
          try {
            dispatch(Store.actions.getDownloadsByCursoStarted())
            const downloads = await api.DownloadsCursos.get({
              query: getState().query,
              cursoId,
            })
            dispatch(Store.actions.getDownloadsByCursoSuccess(downloads))
          } catch (error) {
            dispatch(Store.actions.getDownloadsByCursoError())
          }
        }
      },
      createDownload({ file, cursoId, usuarioId }) {
        return async (dispatch) => {
          dispatch(Store.actions.createDownloadStarted())
          try {
            const formData = new FormData()
            const fileName = file.name || 'arquivo'
            const tipoArquivo = getFileExtension(fileName)

            formData.append('file', file)
            formData.append('nome', fileName)
            formData.append('tipo', 'arquivo')
            formData.append('tipoArquivo', tipoArquivo)
            formData.append('tamanho', String(file.size))
            formData.append('cursoId', String(cursoId))
            formData.append('usuarioId', String(usuarioId))
            formData.append('tipoDocumento', 'APOSTILA')

            const download = await api.DownloadsCursos.create(formData)
            dispatch(Store.actions.downloadCreateOrUpdateSuccess(download))
          } catch (error) {
            toast.error(error?.message || 'Não foi possível enviar o arquivo')
          } finally {
            dispatch(Store.actions.createDownloadFinished())
          }
        }
      },
      deleteDownload({ downloadId }) {
        return async (dispatch) => {
          dispatch(Store.actions.deleteDownloadStarted(downloadId))
          try {
            await api.DownloadsCursos.delete(downloadId)
            dispatch(Store.actions.downloadsDeleteSuccess(downloadId))
          } catch (error) {
            toast.error(error?.message || 'Não foi possível excluir o arquivo')
            dispatch(Store.actions.deleteDownloadError())
          }
        }
      },
    },
  }
)

export const DownloadsCursosView = ({ cursoId }: DownloadsCursosViewProps) => {
  const init = React.useCallback(
    (dispatch: ReturnType<typeof Store.useDispatch>) => {
      dispatch(Store.actions.init({ cursoId }))
      dispatch(Store.thunks.getDownloadsByCurso({ cursoId }))
    },
    [cursoId]
  )

  return (
    <Store.Provider init={init}>
      <SectionCard>
        <CardHeader>
          <SectionTitle>Downloads do curso</SectionTitle>
          <UploadButton />
        </CardHeader>
        <Tabela />
      </SectionCard>
    </Store.Provider>
  )
}

const UploadButton = () => {
  const { cursoId, creatingDownload } = Store.useState()
  const dispatch = Store.useDispatch()
  const { usuario } = useAuthState()
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) {
      return
    }

    if (!usuario?.id) {
      toast.error('Não foi possível identificar o usuário logado')
      return
    }

    dispatch(Store.thunks.createDownload({ file, cursoId, usuarioId: +usuario.id }))
  }

  return (
    <>
      <input ref={fileInputRef} type="file" hidden onChange={onSelectFile} />
      <Button type="primary" loading={creatingDownload} onClick={() => fileInputRef.current?.click()}>
        Enviar novo arquivo
      </Button>
    </>
  )
}

const Tabela = () => {
  const { downloads, fetching } = Store.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (value: string) => value || '-',
    },
    {
      title: 'Ações',
      key: 'action',
      width: 90,
      render: (_, download: DownloadModel) => <DeleteButton downloadId={+download.id} />,
    },
  ]

  return (
    <TableWrapper>
      <Table
        columns={columns}
        dataSource={downloads}
        rowKey="id"
        loading={fetching}
        locale={{ emptyText: 'Nenhum download para o curso ainda.' }}
        pagination={false}
      />
    </TableWrapper>
  )
}

const DeleteButton = ({ downloadId }: { downloadId: number }) => {
  const { deletingDownloadId } = Store.useState()
  const dispatch = Store.useDispatch()
  const isDeleting = deletingDownloadId === downloadId

  return (
    <Popconfirm
      title="Excluir download"
      description="Tem certeza que deseja excluir este arquivo?"
      okText="Excluir"
      cancelText="Cancelar"
      okButtonProps={{ danger: true, loading: isDeleting }}
      onConfirm={() => dispatch(Store.thunks.deleteDownload({ downloadId }))}
    >
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        loading={isDeleting}
        onClick={(e) => e.stopPropagation()}
      />
    </Popconfirm>
  )
}

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.')
  if (parts.length <= 1) {
    return ''
  }

  return parts[parts.length - 1].toLowerCase()
}

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  padding: 20px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #494d50;
`

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  overflow-x: auto;

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr.ant-table-row.ant-table-row-hover > td {
    background: #eeeeee;
  }
`
